import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useDefaultAssignees, useSetDefaultAssignees } from '../../api/default-assignees';

type Kind = 'lead' | 'followUp' | 'task';
const FIELDS: { kind: Kind; label: string; hint: string }[] = [
  {
    kind: 'lead',
    label: 'Leads',
    hint: 'From automations, conversation flows, website chat and quotations',
  },
  {
    kind: 'followUp',
    label: 'Follow-ups',
    hint: 'Reminders for quiet leads and website enquiries',
  },
  { kind: 'task', label: 'Tasks', hint: 'Tasks created automatically' },
];

/**
 * Team → default owners for automatic work: who gets the leads, follow-ups
 * and tasks that automations, flows and the system create (otherwise they
 * stay unassigned). Workspace admins (users.manage).
 */
export function DefaultAssigneesCard() {
  const current = useDefaultAssignees();
  const save = useSetDefaultAssignees();
  const toast = useToast();
  const [form, setForm] = useState<Record<Kind, string>>({ lead: '', followUp: '', task: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!current.data) return;
    setForm({
      lead: current.data.lead?.tenantUserId ?? '',
      followUp: current.data.followUp?.tenantUserId ?? '',
      task: current.data.task?.tenantUserId ?? '',
    });
  }, [current.data]);

  const dirty =
    !!current.data &&
    (form.lead !== (current.data.lead?.tenantUserId ?? '') ||
      form.followUp !== (current.data.followUp?.tenantUserId ?? '') ||
      form.task !== (current.data.task?.tenantUserId ?? ''));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setErrors({});
    try {
      await save.mutateAsync({
        leadAssigneeId: form.lead || null,
        followUpAssigneeId: form.followUp || null,
        taskAssigneeId: form.task || null,
      });
      toast.success('Default owners saved.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const mapped: Record<string, string> = {};
        for (const [path, message] of Object.entries(fieldErrorsFromApiError(err))) {
          mapped[path.replace(/AssigneeId$/, '')] = message;
        }
        if (Object.keys(mapped).length > 0) {
          setErrors(mapped);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not save the default owners.');
    }
  }

  return (
    <Card title="Default owner for automatic work">
      <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-text-muted)' }}>
        Who gets the leads, follow-ups and tasks that automations, conversation flows and the system
        create. They are notified. Leave &quot;Unassigned&quot; to keep them in the shared list.
      </p>
      {current.isPending ? (
        <PageState kind="loading" message="Loading…" />
      ) : current.isError ? (
        <PageState kind="error" message={current.error.message} />
      ) : (
        <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
          {FIELDS.map((f) => (
            <div key={f.kind} style={{ display: 'grid', gap: 2 }}>
              <AssigneeSelect
                label={f.label}
                value={form[f.kind]}
                currentName={current.data[f.kind]?.displayName}
                error={errors[f.kind]}
                onChange={(v) => {
                  setForm((s) => ({ ...s, [f.kind]: v }));
                  setErrors(({ [f.kind]: _gone, ...rest }) => rest);
                }}
              />
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                {f.hint}
              </span>
            </div>
          ))}
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div>
            <Button type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
